import { type CSSProperties, type ReactElement } from "react";
import { Lock } from "lucide-react";
import { Avatar, type AvatarSize } from "@primitives/Avatar";
import { Badge } from "@primitives/Badge";
import { Text } from "@primitives/Text";
import { color, radius, space } from "@tokens/index";

// ---------------------------------------------------------------------------
// PresenceStack — overlapping avatars of everyone present on a record (a
// workflow or a task work item), with a "+N" overflow chip.
//
// Built on the Avatar primitive. The presence hooks supply the roster, ordered
// others-first with the current user's own tokens flagged. Renders nothing
// when no one is present, so it is safe in any header or list row.
//
// Presentational and self-contained: it takes a minimal `PresenceFace` shape
// (a structural subset of the presence module's `PresenceViewer`) so the
// composition stays inside the tokens+primitives boundary. Non-interactive: a
// title on hover and a polite live region answer "who is here?". One member is
// one live connection, so the headcount collapses to distinct people.
// ---------------------------------------------------------------------------

/** Minimal viewer shape this composition renders. A subset of `PresenceViewer`. */
export interface PresenceFace {
    readonly connectionId: string;
    readonly galileoUserId: string;
    readonly displayName: string;
    readonly isSelf: boolean;
}

export interface PresenceStackProps {
    /** Live viewers, ordered others-first with the current user's tokens flagged. */
    readonly members: readonly PresenceFace[];
    /** Max avatars rendered before collapsing the remainder into a "+N" chip. */
    readonly max?: number;
    readonly size?: AvatarSize;
    /** Human label of the record, used in the accessible summary (e.g. "this workflow"). */
    readonly recordLabel?: string;
    /**
     * Galileo user id of the current lock holder (docs/NEXT.md — Locking),
     * if any. When a visible viewer matches, a small lock glyph sits on
     * their avatar so the facepile reads "this person holds the Next lock."
     * At `xs` the glyph is a chip beside the stack instead, named for the
     * holder. A holder who is not present produces no glyph here. The row
     * or card lock chip, driven by the work item's lock fields, is the
     * authoritative indicator.
     */
    readonly lockedGalileoUserId?: string | null;
    /**
     * When true, hides the stack when the only person viewing is the current user
     * without an active lock hold.
     */
    readonly filterSelfWhenAlone?: boolean;
}

/** Faces shown at `xs` before the count chip takes over. */
const XS_MAX_FACES = 2;

const sizePx: Record<AvatarSize, number> = {
    xs: 20,
    sm: 24,
    md: 32,
    lg: 40,
    xl: 56,
};

type SlotStyle = CSSProperties & Record<"--avatar-hue", string>;

// One face per person. Several connections of the same user (two tabs, a
// reconnect that overlaps its predecessor) collapse to the first entry in
// roster order, with the self-flag kept if any of them is self. Faces are
// keyed by user id, so a reconnect does not remount the avatar.
function distinctPeople(members: readonly PresenceFace[]): readonly PresenceFace[] {
    const byUser = new Map<string, PresenceFace>();
    for (const member of members) {
        const seen = byUser.get(member.galileoUserId);
        if (seen === undefined) {
            byUser.set(member.galileoUserId, member);
        } else if (member.isSelf && !seen.isSelf) {
            byUser.set(member.galileoUserId, { ...seen, isSelf: true });
        }
    }
    return [...byUser.values()];
}

const styles = {
    root: {
        display: "inline-flex",
        alignItems: "center",
        gap: space[2],
    } satisfies CSSProperties,
    avatars: {
        display: "inline-flex",
        alignItems: "center",
    } satisfies CSSProperties,
    slot: {
        display: "inline-flex",
        borderRadius: radius.full,
        // The ring is what reads overlapping avatars as a stack rather than a
        // smear: each token punches a canvas-colored gap into the one behind it.
        boxShadow: `0 0 0 2px ${color.bg.canvas}`,
    } satisfies CSSProperties,
    srOnly: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
    } satisfies CSSProperties,
} as const;

export function PresenceStack({
    members,
    max = 4,
    size = "sm",
    recordLabel,
    lockedGalileoUserId,
    filterSelfWhenAlone = false,
}: PresenceStackProps): ReactElement | null {
    if (members.length === 0) {
        return null;
    }

    const hasLock = lockedGalileoUserId != null && lockedGalileoUserId.length > 0;
    if (filterSelfWhenAlone && !hasLock && members.every((member) => member.isSelf)) {
        return null;
    }

    const people = distinctPeople(members);

    // At `xs` a 20px token is too small to read a face or a glyph overlay.
    // Show at most two faces, count the rest, and set the lock beside the
    // stack as a chip instead of on top of an avatar.
    const isTiny = size === "xs";
    const visibleCount = isTiny
        ? Math.min(people.length, XS_MAX_FACES)
        : people.length <= max
          ? people.length
          : max - 1;
    const visible = people.slice(0, visibleCount);
    const overflow = people.length - visibleCount;
    const summary = buildSummary(members, recordLabel);
    const overlap = Math.round(sizePx[size] * 0.3);
    const chipPx = sizePx[size];
    const presentLockHolder = hasLock
        ? (people.find((member) => member.galileoUserId === lockedGalileoUserId) ?? null)
        : null;

    return (
        <div style={styles.root} title={buildTitle(members)}>
            <span style={styles.avatars} aria-hidden="true">
                {visible.map((viewer, index) => {
                    const isLockHolder = !isTiny && hasLock && viewer.galileoUserId === lockedGalileoUserId;
                    const lockGlyphPx = Math.max(12, Math.round(sizePx[size] * 0.5));
                    const slotStyle: SlotStyle = {
                        ...styles.slot,
                        "--avatar-hue": String(color.effect.presenceRing),
                        boxShadow: `0 0 0 2px ${color.bg.canvas}`,
                        position: "relative",
                        marginLeft: index === 0 ? 0 : `-${String(overlap)}px`,
                        // The lock holder sits above its neighbors so the
                        // overlaid glyph is never clipped by the next token.
                        zIndex: isLockHolder ? visible.length + 1 : visible.length - index,
                    };
                    return (
                        <span key={viewer.galileoUserId} style={slotStyle}>
                            <Avatar
                                alt={viewer.displayName}
                                size={size}
                                title={
                                    viewer.isSelf
                                        ? `${viewer.displayName} (you)`
                                        : viewer.displayName
                                }
                            />
                            {isLockHolder ? (
                                <span
                                    style={{
                                        position: "absolute",
                                        top: `-${String(Math.round(lockGlyphPx * 0.25))}px`,
                                        right: `-${String(Math.round(lockGlyphPx * 0.25))}px`,
                                        width: `${String(lockGlyphPx)}px`,
                                        height: `${String(lockGlyphPx)}px`,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: color.accent.primary,
                                        color: color.effect.onAccent,
                                        borderRadius: radius.full,
                                        border: `1.5px solid ${color.bg.canvas}`,
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <Lock
                                        size={Math.max(7, Math.round(lockGlyphPx * 0.6))}
                                        strokeWidth={2.5}
                                        aria-hidden="true"
                                    />
                                </span>
                            ) : null}
                        </span>
                    );
                })}
                {overflow > 0 ? (
                    <span
                        style={{
                            ...styles.slot,
                            // At `xs` the count sits beside the stack as a pill;
                            // a 20px circle under the ring band is not legible.
                            marginLeft: isTiny ? space[1] : `-${String(overlap)}px`,
                            zIndex: 0,
                            width: isTiny ? "auto" : `${String(chipPx)}px`,
                            paddingInline: isTiny ? space[1] : 0,
                            borderRadius: radius.pill,
                            height: `${String(chipPx)}px`,
                            alignItems: "center",
                            justifyContent: "center",
                            background: color.bg.surface2,
                            border: `1px solid ${color.border.subtle}`,
                            color: color.fg.muted,
                        }}
                    >
                        <Text as="span" role="caps" size="xs" style={{ fontSize: "10px" }}>
                            +{String(overflow)}
                        </Text>
                    </span>
                ) : null}
            </span>

            {isTiny && presentLockHolder !== null ? (
                <Badge
                    variant="accent"
                    size="sm"
                    role="img"
                    aria-label={`Locked by ${presentLockHolder.displayName}`}
                    title={`Locked by ${presentLockHolder.displayName}`}
                >
                    <Lock size={10} strokeWidth={2.5} aria-hidden="true" />
                </Badge>
            ) : null}

            {/* Polite live region so screen readers announce roster changes without stealing focus. */}
            <span role="status" aria-live="polite" style={styles.srOnly}>
                {summary}
            </span>
        </div>
    );
}

function buildSummary(members: readonly PresenceFace[], recordLabel?: string): string {
    const distinctPeople = new Set(members.map((member) => member.galileoUserId)).size;
    const noun = distinctPeople === 1 ? "person" : "people";
    const where = recordLabel !== undefined && recordLabel.length > 0 ? ` ${recordLabel}` : "";
    return `${String(distinctPeople)} ${noun} here${where}`;
}

function buildTitle(members: readonly PresenceFace[]): string {
    // Collapse a person's multiple tabs to one line, keeping the "(you)" marker.
    const seen = new Set<string>();
    const lines: string[] = [];
    for (const member of members) {
        if (seen.has(member.galileoUserId)) {
            continue;
        }
        seen.add(member.galileoUserId);
        lines.push(member.isSelf ? `${member.displayName} (you)` : member.displayName);
    }
    return lines.join("\n");
}
